import { forwardRef, useState, useEffect, useRef, useCallback } from 'react';
import { FaPencilAlt, FaSearch } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import { HiX } from 'react-icons/hi';

import { Modal, ModalMethods, Text, toasts } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';

type VariantGroup = {
  id: number;
  name: string;
  values: string[];
};

type EditableGroup = {
  name: string;
  values: string[];
};

type VariantGroupsModalProps = {
  productId: number;
  variantGroups: VariantGroup[];
  suggestions: VariantGroup[];
  onSaved: () => void;
};

const COLOR_HEX_MAP: Record<string, string> = {
  white: '#FFFFFF',
  black: '#000000',
  red: '#EF4444',
  blue: '#3B82F6',
  green: '#22C55E',
  yellow: '#EAB308',
  orange: '#F97316',
  pink: '#EC4899',
  purple: '#A855F7',
  brown: '#92400E',
  grey: '#9CA3AF',
  gray: '#9CA3AF',
  navy: '#1E3A5F',
  maroon: '#800000',
  beige: '#F5F5DC',
  gold: '#D4A017',
  silver: '#C0C0C0',
};

const getColorHex = (name: string): string => {
  return COLOR_HEX_MAP[name.toLowerCase()] || '#9CA3AF';
};

const isColorGroup = (name: string): boolean => {
  const lower = name.toLowerCase();

  return lower === 'color' || lower === 'colour' || lower === 'colors' || lower === 'colours';
};

const VariantGroupsModal = forwardRef<ModalMethods, VariantGroupsModalProps>(
  ({ productId, variantGroups, suggestions, onSaved }, ref) => {
    const [groups, setGroups] = useState<EditableGroup[]>([]);
    const [activeTabIdx, setActiveTabIdx] = useState(0);
    const [groupNameSearch, setGroupNameSearch] = useState('');
    const [isGroupNameOpen, setIsGroupNameOpen] = useState(false);
    const [optionInput, setOptionInput] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const optionInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      const editable: EditableGroup[] = variantGroups.map((g) => ({
        name: g.name,
        values: [...g.values],
      }));

      setGroups(editable);
      setActiveTabIdx(0);
      setGroupNameSearch('');
      setOptionInput('');
    }, [variantGroups]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsGroupNameOpen(false);
        }
      };

      if (isGroupNameOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isGroupNameOpen]);

    const activeGroup = groups[activeTabIdx] || null;

    const filteredSuggestions = suggestions.filter((s) => {
      if (!groupNameSearch.trim()) {
        return true;
      }

      return s.name.toLowerCase().includes(groupNameSearch.toLowerCase());
    });

    const exactMatch = suggestions.some(
      (s) => s.name.toLowerCase() === groupNameSearch.trim().toLowerCase()
    );

    const handleSelectSuggestion = useCallback(
      (suggestion: VariantGroup) => {
        setGroups((prev) => {
          const updated = [...prev];

          updated[activeTabIdx] = {
            name: suggestion.name,
            values: [...suggestion.values],
          };

          return updated;
        });
        setGroupNameSearch('');
        setIsGroupNameOpen(false);
      },
      [activeTabIdx]
    );

    const handleCreateGroup = useCallback(
      (name: string) => {
        setGroups((prev) => {
          const updated = [...prev];

          updated[activeTabIdx] = {
            name: name.trim(),
            values: updated[activeTabIdx]?.values ?? [],
          };

          return updated;
        });
        setGroupNameSearch('');
        setIsGroupNameOpen(false);
      },
      [activeTabIdx]
    );

    const handleAddNewGroupTab = useCallback(() => {
      setGroups((prev) => [...prev, { name: '', values: [] }]);
      setActiveTabIdx(groups.length);
      setGroupNameSearch('');
      setOptionInput('');
    }, [groups.length]);

    const handleDeleteGroupTab = useCallback(
      (idx: number) => {
        setGroups((prev) => {
          const updated = prev.filter((_, i) => i !== idx);

          return updated;
        });

        if (activeTabIdx >= idx && activeTabIdx > 0) {
          setActiveTabIdx((prev) => prev - 1);
        }
      },
      [activeTabIdx]
    );

    const handleRemoveOption = useCallback(
      (value: string) => {
        setGroups((prev) => {
          const updated = [...prev];
          const group = { ...updated[activeTabIdx] };

          group.values = group.values.filter((v) => v !== value);
          updated[activeTabIdx] = group;

          return updated;
        });
      },
      [activeTabIdx]
    );

    const handleAddOption = useCallback(
      (value: string) => {
        const trimmed = value.trim();

        if (!trimmed) {
          return;
        }

        setGroups((prev) => {
          const updated = [...prev];
          const group = { ...updated[activeTabIdx] };

          if (group.values.some((v) => v.toLowerCase() === trimmed.toLowerCase())) {
            toasts('error', 'This option already exists', 'duplicate-option');

            return prev;
          }

          group.values = [...group.values, trimmed];
          updated[activeTabIdx] = group;

          return updated;
        });
        setOptionInput('');
      },
      [activeTabIdx]
    );

    const handleOptionKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
          e.preventDefault();
          handleAddOption(optionInput);
        }

        if (
          e.key === 'Backspace' &&
          optionInput === '' &&
          activeGroup &&
          activeGroup.values.length > 0
        ) {
          handleRemoveOption(activeGroup.values[activeGroup.values.length - 1]);
        }
      },
      [optionInput, handleAddOption, handleRemoveOption, activeGroup]
    );

    const handleOptionChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        if (value.includes(',') || value.includes(' ')) {
          const parts = value.split(/[, ]+/);

          parts.forEach((part) => {
            if (part.trim()) {
              handleAddOption(part);
            }
          });

          return;
        }

        setOptionInput(value);
      },
      [handleAddOption]
    );

    const handleSave = async () => {
      const validGroups = groups.filter((g) => g.name.trim() && g.values.length > 0);

      if (validGroups.length === 0) {
        toasts('error', 'Add at least one group with options', 'save-variant-groups-empty');

        return;
      }

      const hasEmptyName = groups.some((g) => !g.name.trim() && g.values.length > 0);

      if (hasEmptyName) {
        toasts('error', 'All groups must have a name', 'save-variant-groups-no-name');

        return;
      }

      try {
        setIsSaving(true);

        const payload = validGroups.map((g) => ({
          name: g.name,
          values: g.values,
        }));

        const response = await api.postRequest(`readymade/${productId}/variant-groups`, payload);

        if (response.status) {
          toasts('success', 'Variant groups saved', 'variant-groups-saved');

          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }

          onSaved();
        } else {
          toasts(
            'error',
            response.message || 'Failed to save variant groups',
            'variant-groups-save-error'
          );
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'variant-groups-save-error');
        }
      } finally {
        setIsSaving(false);
      }
    };

    const headerComponent = (
      <div className="flex items-center justify-between w-full pr-8">
        <Text size="large" fontWeight={700} className="font-inter">
          Edit, delete or add new group
        </Text>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-blue-700 transition-colors disabled:opacity-60"
          >
            Save
            <span>💾</span>
          </button>
          <button
            type="button"
            onClick={() => {
              if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
                ref.current.hide();
              }
            }}
            className="flex items-center gap-1.5 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <Text size="small" color="black" className="font-inter">
              Close
            </Text>
            <HiX size={20} />
          </button>
        </div>
      </div>
    );

    return (
      <Modal
        ref={ref}
        size="xl"
        headerComponent={headerComponent}
        showFooter={false}
        showCloseIcon={false}
      >
        <div className="flex flex-col min-h-[420px] py-4">
          <div className="flex items-center gap-2 mb-6 flex-wrap justify-center">
            {groups.map((group, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => {
                  setActiveTabIdx(idx);
                  setGroupNameSearch('');
                  setOptionInput('');
                }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeTabIdx === idx
                    ? 'bg-black text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-gray-400'
                }`}
              >
                {group.name || `Group ${idx + 1}`}
              </button>
            ))}
            <button
              type="button"
              onClick={handleAddNewGroupTab}
              className="px-4 py-2 rounded-full text-sm font-medium bg-white text-gray-700 border border-gray-300 hover:border-gray-400 transition-colors"
            >
              Add New Group
            </button>
          </div>

          {activeGroup && (
            <div className="space-y-6">
              <div>
                <p className="font-inter font-bold text-[1rem] mb-2">Group Name</p>
                <div ref={dropdownRef} className="relative">
                  <div
                    className="border border-gray-300 rounded-lg p-3 flex items-center gap-2 cursor-text"
                    onClick={() => setIsGroupNameOpen(true)}
                  >
                    <FaSearch size={14} className="text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      value={isGroupNameOpen ? groupNameSearch : activeGroup.name}
                      onChange={(e) => {
                        setGroupNameSearch(e.target.value);

                        if (!isGroupNameOpen) {
                          setIsGroupNameOpen(true);
                        }
                      }}
                      onFocus={() => {
                        setGroupNameSearch(activeGroup.name);
                        setIsGroupNameOpen(true);
                      }}
                      placeholder="select or create group"
                      className="flex-1 outline-none text-sm font-inter"
                    />
                    <span className="text-xs text-gray-400">select or create group</span>
                  </div>

                  {isGroupNameOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-[250px] overflow-y-auto">
                      {filteredSuggestions.map((suggestion) => (
                        <div
                          key={suggestion.id}
                          className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 cursor-pointer group"
                        >
                          <button
                            type="button"
                            onClick={() => handleSelectSuggestion(suggestion)}
                            className="flex-1 text-left text-sm font-inter font-medium text-gray-800"
                          >
                            {suggestion.name}
                          </button>
                          <div className="flex items-center gap-2 opacity-70 group-hover:opacity-100">
                            <button
                              type="button"
                              className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                            >
                              <FaPencilAlt size={12} />
                            </button>
                            <button
                              type="button"
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                            >
                              <MdDelete size={16} />
                            </button>
                          </div>
                        </div>
                      ))}

                      {groupNameSearch.trim() && !exactMatch && (
                        <button
                          type="button"
                          onClick={() => handleCreateGroup(groupNameSearch)}
                          className="w-full px-4 py-2.5 text-left text-sm font-inter font-medium text-green-700 bg-green-50 hover:bg-green-100"
                        >
                          +Create &quot;{groupNameSearch.trim()}&quot;
                        </button>
                      )}

                      {filteredSuggestions.length === 0 && !groupNameSearch.trim() && (
                        <div className="px-4 py-3 text-center text-sm text-gray-400">
                          No suggestions available
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <p className="font-inter font-bold text-[1rem] mb-2">Options</p>
                <div
                  className="border border-gray-300 rounded-lg p-3 flex flex-wrap items-center gap-2 min-h-[48px] cursor-text"
                  onClick={() => optionInputRef.current?.focus()}
                >
                  <FaSearch size={14} className="text-gray-400 flex-shrink-0" />
                  {activeGroup.values.map((val) => (
                    <span
                      key={val}
                      className="inline-flex items-center gap-1.5 bg-gray-100 rounded-full pl-2 pr-1 py-1 text-sm font-inter"
                    >
                      {isColorGroup(activeGroup.name) && (
                        <span
                          className="w-4 h-4 rounded-full border border-gray-300 inline-block flex-shrink-0"
                          style={{ backgroundColor: getColorHex(val) }}
                        />
                      )}
                      <span className="font-medium text-gray-800">{val}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveOption(val);
                        }}
                        className="w-5 h-5 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors"
                      >
                        <HiX size={14} />
                      </button>
                    </span>
                  ))}
                  <input
                    ref={optionInputRef}
                    type="text"
                    value={optionInput}
                    onChange={handleOptionChange}
                    onKeyDown={handleOptionKeyDown}
                    placeholder={
                      activeGroup.values.length === 0
                        ? 'Type and press Enter, Space or Comma to add'
                        : ''
                    }
                    className="flex-1 min-w-[120px] outline-none text-sm font-inter"
                  />
                </div>
              </div>

              {groups.length > 1 && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => handleDeleteGroupTab(activeTabIdx)}
                    className="text-sm text-red-500 hover:text-red-700 font-medium flex items-center gap-1.5 transition-colors"
                  >
                    <MdDelete size={16} />
                    Delete this group
                  </button>
                </div>
              )}
            </div>
          )}

          {!activeGroup && groups.length === 0 && (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-3">No variant groups yet.</p>
                <button
                  type="button"
                  onClick={handleAddNewGroupTab}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  + Add Group
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    );
  }
);

VariantGroupsModal.displayName = 'VariantGroupsModal';

export default VariantGroupsModal;
